// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API endpoint for retrieving detail inconsistencies in assets.
 *
 * Provides functionality to fetch assets with detail inconsistencies such as
 * missing cost basis, salvage value issues, missing useful life, etc.
 */
import { api } from '@/api/axios-instance';
import { responseMetadataSchema } from '@/api/types';
import { ExceptionType } from '@/constants/asset-exception-type';
import { detailInconsistencyItemSchema } from '@/report/api/types';
import { parseArrayInChunks } from '@/utilities/chunked-parse';
import queryString from 'query-string';
import { z } from 'zod';

export const detailInconsistenciesResponseSchema = z.object({
  data: z.object({
    assets: z.array(detailInconsistencyItemSchema),
    totalAnalyzed: z.number(),
  }),
  metadata: responseMetadataSchema,
});

type DetailInconsistenciesResponse = z.infer<
  typeof detailInconsistenciesResponseSchema
>;

export interface DetailInconsistenciesParameters {
  exceptionType?: ExceptionType;
  assetIds?: string[];
}

/**
 * Retrieves assets with detail inconsistencies.
 *
 * @param params - The parameters for the request
 * @returns A promise that resolves to the detail inconsistencies data
 */
export async function getDetailInconsistencies(
  params?: DetailInconsistenciesParameters
) {
  const path = '/v1/report/exception/asset/detail';
  const response = await api.get<DetailInconsistenciesResponse>(path, {
    paramsSerializer: (parameters) => queryString.stringify(parameters),
    params: {
      exceptionType: params?.exceptionType,
      assetIds: params?.assetIds,
    },
  });

  // Parse assets array in chunks to prevent UI thread blocking
  const assets = await parseArrayInChunks(
    response.data.data.assets,
    detailInconsistencyItemSchema
  );

  return {
    assets,
    totalAnalyzed: response.data.data.totalAnalyzed,
  };
}
